import {
  CustomerInfo,
  LineOfCreditSoftCreditCheckError,
  LineOfCreditSoftCreditCheckSuccess,
} from '@varo/graphql-schema-types';
import { format, isValid, parseISO } from 'date-fns';
import {
  NativeStackHeaderProps,
  Stack,
  useFocusEffect,
  useRouter,
} from 'expo-router';
import LineOfCreditErrorScreenComponent, {
  VlocStepName,
} from 'features/lineOfCredit/screens/error/LineOfCreditErrorScreenComponent';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import useGetProfileInfo from 'shared/hooks/useGetProfileInfo/useGetProfileInfo';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { getUuid } from 'shared/utils/getUuid/getUuid';

import useGetLineOfCreditSoftPullCompleteStatus from './hooks/useGetLineOfCreditSoftPullCompleteStatus';
import useLineOfCreditSoftCreditCheck from './hooks/useLineOfCreditSoftCreditCheck';
import useSoftCheckPollingEffect from './hooks/useSoftCheckPollingEffect';

interface ListItemData {
  label: string;
  value: string;
}

const UserInfoScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('vloc');

  const { bottom } = useSafeAreaInsets();

  const customAttributes = { credit_check_type: 'soft' };

  const { error, fetching, result, refetch } = useGetProfileInfo();

  const {
    error: creditCheckError,
    fetching: creditCheckLoading,
    result: creditCheckResult,
    triggerMutation,
  } = useLineOfCreditSoftCreditCheck();

  const { result: softCheckResult, refetchCheck } =
    useGetLineOfCreditSoftPullCompleteStatus();

  const goBack = () => {
    router.back();
  };

  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  const { isChecking, attempts, showError, setIsChecking, setShowError } =
    useSoftCheckPollingEffect(softCheckResult, refetchCheck);

  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch]),
  );

  useEffect(() => {
    if (creditCheckResult as LineOfCreditSoftCreditCheckSuccess) {
      setIsChecking(true);
    } else if (creditCheckResult as LineOfCreditSoftCreditCheckError) {
      setShowError(true);
    }
  }, [creditCheckResult, setIsChecking, setShowError]);

  const customerInfo = result?.customerInfo as CustomerInfo | undefined;

  const formattedAddress = `${customerInfo?.address?.addressLine1 ?? ''},${customerInfo?.address?.addressLine2 ?? ''} ${customerInfo?.address?.city ?? ''}, ${customerInfo?.address?.state ?? ''} ${customerInfo?.address?.zip ?? ''}`;

  const dob = parseISO(customerInfo?.dateOfBirth ?? '');

  const listItems: ListItemData[] = [
    {
      label: translatedText('qualificationCheck.listItems.legalName'),
      value: customerInfo?.name?.full ?? '',
    },
    {
      label: translatedText('qualificationCheck.listItems.homeAddress'),
      value: formattedAddress,
    },
    {
      label: translatedText('qualificationCheck.listItems.ssn'),
      value: customerInfo?.ssnLastFour ?? '',
    },
    {
      label: translatedText('qualificationCheck.listItems.dateOfBirth'),
      value:
        customerInfo?.dateOfBirth && isValid(dob)
          ? format(dob, 'MM/dd/yyyy')
          : '',
    },
  ];

  const softCreditCheck = () => {
    triggerMutation(getUuid());
  };

  const hasError = !!(error || creditCheckError || showError);
  const isLoading = fetching || creditCheckLoading;

  const getBackHandler = () => {
    if (isChecking) {
      return () => null;
    }

    return hasError ? goHome : goBack;
  };

  useBackHandler(getBackHandler());

  useViewDidFocusScreenAnalyticsEvent(
    isChecking
      ? {
          key: 'vlocCreditCheck',
          customAttributes: customAttributes,
        }
      : null,
  );

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
          headerShown: !isChecking && !hasError,
          gestureEnabled: !isChecking && !hasError,
          headerBackVisible: !isChecking && !hasError,
        }}
      />
      {isChecking ? (
        <View style={styles.loaderStyle} testID="fullScreenLoader">
          <FullScreenLoading
            isCoinLoader
            titleText={translatedText('crunchingTheNumbers.title')}
            bodyText={translatedText('crunchingTheNumbers.subTitle')}
          />
        </View>
      ) : (
        attempts === 0 && (
          <View style={styles.contentWrapper}>
            <ScrollView contentContainerStyle={styles.scrollViewContent}>
              <TitleLgText style={styles.titleText} numberOfLines={0}>
                {translatedText('qualificationCheck.title')}
              </TitleLgText>
              <BodyBaseText style={styles.bodyText} numberOfLines={0}>
                {translatedText('qualificationCheck.instruction')}
              </BodyBaseText>
              <View style={styles.listContainer}>
                {listItems?.map((item, index) => (
                  <View key={item.label}>
                    <ListItem
                      primaryCopy={item.value}
                      secondaryCopy={item.label}
                      lineFormat={ListItemLineFormat.LABEL}
                      trailingAccessory={
                        index === 1
                          ? {
                              id: 1,
                              name: VXLComponentId.ICON,
                              props: {
                                iconKey: 'EDIT',
                              },
                            }
                          : undefined
                      }
                      onPress={
                        index === 1
                          ? () =>
                              router.push({
                                pathname:
                                  '/myvaro/personal-details/change-address',
                                params: { isVloc: 'true' },
                              })
                          : undefined
                      }
                      showsChevron={false}
                      analyticsId={
                        index === 1 ? 'editAddressClicked' : undefined
                      }
                    />
                    <View style={styles.dividerContainer}>
                      {index < listItems.length - 1 ? (
                        <HairLineDivider />
                      ) : null}
                    </View>
                  </View>
                ))}
              </View>
            </ScrollView>
            <View style={styles.footer}>
              <BodyXSText style={styles.disclaimer} numberOfLines={0}>
                {translatedText('qualificationCheck.disclaimer')}
              </BodyXSText>
              <View style={styles.buttonContainer}>
                <Button
                  label={translatedText('qualificationCheck.buttons.confirm')}
                  type="PRIMARY"
                  onPress={softCreditCheck}
                  analyticsId="confirmClicked"
                />
                <View style={styles.cancelButton}>
                  <Button
                    label={translatedText('qualificationCheck.buttons.cancel')}
                    type="SECONDARY"
                    onPress={goHome}
                    analyticsId="cancelClicked"
                  />
                </View>
              </View>
            </View>
            <ScreenTransitionOverlay
              isLoading={isLoading}
              LoadingComponent={<LoadingSpinner />}
              ErrorComponent={
                hasError ? (
                  <LineOfCreditErrorScreenComponent
                    screenAnalyticsId="/varo-line-of-credit/LineOfCreditErrorScreenComponent-userInfoScreen"
                    trackingAnalyticsId="user-info-error-screen-tracking"
                    trackingCustomAttributes={{
                      step_name:
                        creditCheckError || showError
                          ? VlocStepName.SOFT_CREDIT_CHECK
                          : VlocStepName.USER_INFO,
                    }}
                  />
                ) : null
              }
            />
          </View>
        )
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  bodyText: {
    paddingHorizontal: theme.spacing.m,
  },
  buttonContainer: {
    padding: theme.spacing.m,
  },
  cancelButton: {
    marginTop: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  contentWrapper: {
    flex: 1,
    justifyContent: 'space-between',
  },
  disclaimer: { paddingHorizontal: theme.spacing.m },
  dividerContainer: {
    paddingHorizontal: theme.spacing.m,
  },
  footer: {
    backgroundColor: theme.color.background.primary,
  },
  listContainer: {
    paddingVertical: theme.spacing.m,
  },
  loaderStyle: {
    flex: 1,
  },
  scrollViewContent: {
    paddingBottom: theme.spacing.m,
  },
  titleText: {
    padding: theme.spacing.m,
  },
});

export default UserInfoScreen;
